import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { HexColorPicker } from 'react-colorful';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { useUpdateBubbleColorsMutation } from 'store/services/user/meApi';
import { toast } from 'react-toastify';
import './BubbleColorPickerModal.css';

interface BubbleColorPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentColor1: string;
  currentColor2: string;
  onSaveOverride?: () => void;
}

const DEFAULT_COLOR_1 = '#8B5CF6';
const DEFAULT_COLOR_2 = '#6D28D9';

const PRESETS = [
  { label: 'Default', color1: '#8B5CF6', color2: '#6D28D9' },
  { label: 'Pearl', color1: '#958F8F', color2: '#7C6C32' },
  { label: 'Sapphire', color1: '#2D53C4', color2: '#676C8A' },
  { label: 'Jet', color1: '#332826', color2: '#685A65' },
  { label: 'Ruby', color1: '#AC2248', color2: '#3F181F' },
  { label: 'Emerald', color1: '#10B981', color2: '#047857' },
  { label: 'Amber', color1: '#F59E0B', color2: '#B45309' },
];

const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;

const supportsEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;

/** Convert hex to HSL. Returns [h, s, l] where h is 0-360, s/l are 0-100 */
function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) return [0, 0, l * 100];

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return [h * 360, s * 100, l * 100];
}

/** Returns true if the color is in the red range and should be blocked */
function isRedRange(hex: string): boolean {
  if (!HEX_REGEX.test(hex)) return false;
  const [h, s] = hexToHsl(hex);
  return (h <= 15 || h >= 345) && s > 50;
}

const BubbleColorPickerModal: React.FC<BubbleColorPickerModalProps> = ({
  isOpen,
  onClose,
  currentColor1,
  currentColor2,
  onSaveOverride,
}) => {
  const [color1, setColor1] = useState(currentColor1);
  const [color2, setColor2] = useState(currentColor2);
  const [hex1, setHex1] = useState(currentColor1);
  const [hex2, setHex2] = useState(currentColor2);
  const [hex1Error, setHex1Error] = useState(false);
  const [hex2Error, setHex2Error] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [updateBubbleColors] = useUpdateBubbleColorsMutation();

  const redBlocked = isRedRange(color1) || isRedRange(color2);

  // Sync state when modal opens
  useEffect(() => {
    if (isOpen) {
      setColor1(currentColor1);
      setColor2(currentColor2);
      setHex1(currentColor1);
      setHex2(currentColor2);
      setHex1Error(false);
      setHex2Error(false);
    }
  }, [isOpen, currentColor1, currentColor2]);

  const updateColor1 = useCallback((c: string) => {
    setColor1(c);
    setHex1(c);
    setHex1Error(false);
  }, []);

  const updateColor2 = useCallback((c: string) => {
    setColor2(c);
    setHex2(c);
    setHex2Error(false);
  }, []);

  const handleHex1Change = (value: string) => {
    const v = value.startsWith('#') ? value : '#' + value;
    setHex1(v);
    if (HEX_REGEX.test(v)) {
      setColor1(v.toUpperCase());
      setHex1Error(false);
    }
  };

  const handleHex1Blur = () => {
    if (!HEX_REGEX.test(hex1)) setHex1Error(true);
  };

  const handleHex2Change = (value: string) => {
    const v = value.startsWith('#') ? value : '#' + value;
    setHex2(v);
    if (HEX_REGEX.test(v)) {
      setColor2(v.toUpperCase());
      setHex2Error(false);
    }
  };

  const handleHex2Blur = () => {
    if (!HEX_REGEX.test(hex2)) setHex2Error(true);
  };

  const handleEyeDropper = async (setColor: (c: string) => void) => {
    try {
      const dropper = new (window as any).EyeDropper();
      const result = await dropper.open();
      setColor(result.sRGBHex.toUpperCase());
    } catch {
      // User cancelled
    }
  };

  const handlePreset = (c1: string, c2: string) => {
    updateColor1(c1.toUpperCase());
    updateColor2(c2.toUpperCase());
  };

  const handleReset = () => {
    updateColor1(DEFAULT_COLOR_1);
    updateColor2(DEFAULT_COLOR_2);
  };

  const handleSave = async () => {
    if (onSaveOverride) {
      onClose();
      onSaveOverride();
      return;
    }
    if (redBlocked) return;
    setIsSaving(true);
    try {
      await updateBubbleColors({ color1, color2 }).unwrap();
      toast.success('Bubble colors saved!');
      onClose();
    } catch {
      toast.error('Failed to save bubble colors. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const isPresetActive = (c1: string, c2: string) =>
    color1.toUpperCase() === c1.toUpperCase() && color2.toUpperCase() === c2.toUpperCase();

  if (!isOpen) return null;

  const gradientStyle = { background: `linear-gradient(135deg, ${color1}, ${color2})` };

  return createPortal(
    <div className="bubble-color-modal-overlay" onClick={onClose}>
      <div className="bubble-color-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bubble-color-modal-header">
          <h3>
            Customize Chat Bubble Color
            <img
              src={AssetUrls.icons.elite.badge}
              alt="Elite"
              width={20}
              height={20}
              style={{ marginLeft: 8, verticalAlign: 'middle' }}
            />
          </h3>
          <button className="bubble-color-modal-close" onClick={onClose}>
            <img src={AssetUrls.icons.dropdowns.close} alt="Close" />
          </button>
        </div>

        {/* Body */}
        <div className="bubble-color-modal-body">
          {/* Live Preview - Mini conversation */}
          <div className="bubble-color-preview">
            <span className="bubble-color-preview-label">Live Preview</span>
            <div className="bubble-color-preview-chat">
              <div className="bubble-preview-msg bubble-preview-received">
                <div className="bubble-preview-bubble" style={{ background: `linear-gradient(135deg, ${DEFAULT_COLOR_1}, ${DEFAULT_COLOR_2})` }}>
                  Hey, want to trade?
                </div>
              </div>
              <div className="bubble-preview-msg bubble-preview-received">
                <div className="bubble-preview-bubble" style={{ background: `linear-gradient(135deg, ${DEFAULT_COLOR_1}, ${DEFAULT_COLOR_2})` }}>
                  I have some great cards
                </div>
              </div>
              <div className="bubble-preview-msg bubble-preview-sent">
                <div className="bubble-preview-bubble" style={gradientStyle}>
                  Sure, let me check!
                </div>
              </div>
            </div>
          </div>

          {/* Red warning */}
          {redBlocked && (
            <div className="bubble-color-red-warning">
              Red-range colors are not allowed. Please pick a different hue.
            </div>
          )}

          {/* Presets */}
          <div className="bubble-color-presets">
            <div className="bubble-color-presets-label">Presets</div>
            <div className="bubble-color-presets-row">
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  className={`bubble-color-preset ${isPresetActive(preset.color1, preset.color2) ? 'bubble-color-preset--active' : ''}`}
                  onClick={() => handlePreset(preset.color1, preset.color2)}
                >
                  <div
                    className="bubble-color-preset-swatch"
                    style={{
                      background: `linear-gradient(135deg, ${preset.color1}, ${preset.color2})`,
                    }}
                  />
                  <span className="bubble-color-preset-name">{preset.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Color Pickers */}
          <div className="bubble-color-pickers">
            {/* Color 1 */}
            <div className="bubble-color-picker-column">
              <div className="bubble-color-picker-label">Color 1</div>
              <HexColorPicker color={color1} onChange={updateColor1} />
              <div className="bubble-color-hex-row">
                <div
                  className="bubble-color-swatch-preview"
                  style={{ backgroundColor: color1 }}
                />
                <input
                  type="text"
                  className={`bubble-color-hex-input ${hex1Error ? 'bubble-color-hex-input--error' : ''}`}
                  value={hex1}
                  onChange={(e) => handleHex1Change(e.target.value)}
                  onBlur={handleHex1Blur}
                  maxLength={7}
                  placeholder="#8B5CF6"
                />
                {supportsEyeDropper && (
                  <button
                    className="bubble-color-eyedropper"
                    onClick={() => handleEyeDropper(updateColor1)}
                    title="Pick color from screen"
                  >
                    &#128065;
                  </button>
                )}
              </div>
              {hex1Error && <div className="bubble-color-hex-error">Invalid hex code</div>}
            </div>

            {/* Color 2 */}
            <div className="bubble-color-picker-column">
              <div className="bubble-color-picker-label">Color 2</div>
              <HexColorPicker color={color2} onChange={updateColor2} />
              <div className="bubble-color-hex-row">
                <div
                  className="bubble-color-swatch-preview"
                  style={{ backgroundColor: color2 }}
                />
                <input
                  type="text"
                  className={`bubble-color-hex-input ${hex2Error ? 'bubble-color-hex-input--error' : ''}`}
                  value={hex2}
                  onChange={(e) => handleHex2Change(e.target.value)}
                  onBlur={handleHex2Blur}
                  maxLength={7}
                  placeholder="#6D28D9"
                />
                {supportsEyeDropper && (
                  <button
                    className="bubble-color-eyedropper"
                    onClick={() => handleEyeDropper(updateColor2)}
                    title="Pick color from screen"
                  >
                    &#128065;
                  </button>
                )}
              </div>
              {hex2Error && <div className="bubble-color-hex-error">Invalid hex code</div>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bubble-color-modal-footer">
          <button className="bubble-color-reset-link" onClick={handleReset}>
            Reset to Default
          </button>
          <MagicButton label="Cancel" color="dark-grey" onPress={onClose} />
          <MagicButton label="Save" color="purple" onPress={handleSave} isLoading={isSaving} isDisabled={redBlocked} />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BubbleColorPickerModal;
